import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import {
  LayoutDashboard, Building2, MessageSquare,
  LogOut, Shield, CheckCircle
} from 'lucide-react';

const NAV = [
  { path: '/manager/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/manager/companies', icon: Building2, label: 'My Companies' },
  { path: '/manager/tickets', icon: MessageSquare, label: 'Tickets' },
];

export default function ManagerLayout({ children, manager }) {
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/manager/login');
  };

  const isEnterprise = manager?.tier === 'enterprise';

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#F8FAFF' }}>
      {/* Sidebar */}
      <aside style={{
        width: 240, background: '#0F172A',
        display: 'flex', flexDirection: 'column',
        position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 100,
      }}>
        {/* Logo */}
        <div style={{
          padding: '20px', borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <img src="/universe.png" alt="Universe"
              style={{ width: 36, height: 36, objectFit: 'contain' }}
              onError={e => e.target.style.display = 'none'} />
            <div>
              <div style={{
                fontWeight: 800, fontSize: 13,
                background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                letterSpacing: 1,
              }}>UNIVERSE</div>
              <div style={{ fontSize: 9, color: '#475569' }}>Manager Portal</div>
            </div>
          </div>

          {/* Manager info */}
          <div style={{
            background: 'rgba(255,255,255,0.04)',
            borderRadius: 10, padding: '10px 12px',
            border: `1px solid ${isEnterprise ? 'rgba(245,158,11,0.2)' : 'rgba(79,142,247,0.15)'}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: manager?.avatar_color || '#4F8EF7',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontWeight: 800, fontSize: 14,
              }}>
                {manager?.name?.[0] || 'M'}
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#F1F5F9' }}>
                  {manager?.name || 'Manager'}
                </div>
                <div style={{
                  fontSize: 9, fontWeight: 700,
                  color: isEnterprise ? '#F59E0B' : '#4F8EF7',
                }}>
                  {isEnterprise ? '👑 ENTERPRISE' : '✨ STANDARD'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '12px 10px' }}>
          {NAV.map(({ path, icon: Icon, label }) => {
            const active = location.pathname === path;
            return (
              <Link key={path} to={path} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', borderRadius: 10, marginBottom: 2,
                background: active ? 'rgba(79,142,247,0.15)' : 'transparent',
                color: active ? '#4F8EF7' : '#64748B',
                fontWeight: active ? 700 : 500, fontSize: 13,
                textDecoration: 'none', transition: 'all 0.15s',
                borderLeft: active ? '3px solid #4F8EF7' : '3px solid transparent',
              }}
                onMouseEnter={e => {
                  if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                }}
                onMouseLeave={e => {
                  if (!active) e.currentTarget.style.background = 'transparent';
                }}>
                <Icon size={17} />{label}
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div style={{ padding: '12px 10px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ fontSize: 11, color: '#475569', padding: '0 12px 8px' }}>
            {manager?.current_companies || 0} / {manager?.max_companies || 30} companies
          </div>
          <div style={{
            height: 4, background: '#1E293B',
            borderRadius: 2, margin: '0 12px 12px',
          }}>
            <div style={{
              height: '100%', borderRadius: 2,
              background: 'linear-gradient(90deg,#4F8EF7,#7C3AED)',
              width: `${Math.min(100, ((manager?.current_companies || 0) / (manager?.max_companies || 30)) * 100)}%`,
            }} />
          </div>
          <button onClick={handleLogout} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 12px', borderRadius: 10,
            background: 'transparent', border: 'none',
            color: '#EF4444', fontWeight: 600, fontSize: 13,
            cursor: 'pointer', width: '100%',
          }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.08)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <LogOut size={17} />Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main style={{ marginLeft: 240, flex: 1, minHeight: '100vh', background: '#F8FAFF' }}>
        <div style={{
          background: '#fff', borderBottom: '1px solid #E2E8F0',
          padding: '14px 32px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, zIndex: 50,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src="/universe.png" alt="Universe"
              style={{ height: 24, objectFit: 'contain' }}
              onError={e => e.target.style.display = 'none'} />
            <span style={{ fontSize: 13, color: '#94A3B8' }}>
              manager.inncatechnologies.com
            </span>
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '5px 12px',
            background: isEnterprise ? 'rgba(245,158,11,0.08)' : 'rgba(79,142,247,0.08)',
            borderRadius: 8,
            border: `1px solid ${isEnterprise ? 'rgba(245,158,11,0.2)' : 'rgba(79,142,247,0.2)'}`,
          }}>
            <Shield size={13} color={isEnterprise ? '#F59E0B' : '#4F8EF7'} />
            <span style={{
              fontSize: 12, fontWeight: 700,
              color: isEnterprise ? '#F59E0B' : '#4F8EF7',
            }}>
              {isEnterprise ? 'Enterprise Manager' : 'Support Manager'}
            </span>
          </div>
        </div>
        <div style={{ padding: 32 }}>{children}</div>
      </main>
    </div>
  );
}